import { Button, Flex, Heading3, InputText } from "@goodfill/ui-components";
import { useState } from "react";
import { FieldGroup } from "../../../form/FieldGroup";
import { ForgotPasswordLockIcon } from "./ForgotPasswordLockIcon";
import { zodErrorsToString } from "../../../form/zod/zodErrorsToString";
import { zPassword } from "../../../form/zod/password";
import { ConfirmationCodeForm, CONFIRMATION_CODE_SIZE } from "../Confirmation/ConfirmationCodeForm";
import { FormValidationResult } from "../../../form/formValidation";
import { forgotPasswordConfirm, signIn } from "@goodfill/backend-api-client";
import { backendFetcher } from "../../backendApi/backendApi";
import { toastErrorHandler } from "../../../unexpectedErrorHandler";
import { Auth } from "aws-amplify";
import { route } from "../../../routes";
import { mapLoginDataToUserState } from "../mapLoginDataToUserState";
import { useRecoilState } from "recoil";
import { userAuthState } from "../../../state";
import { useNavigate } from "react-router";
import { useLogout } from "../../useLogout";

export interface ForgotPasswordConfirmationFormProps {
  username: string;
}

export const ForgotPasswordConfirmationForm: React.FC<ForgotPasswordConfirmationFormProps> = ({ username }) => {
  const [, setUserAuthState] = useRecoilState(userAuthState);
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);

  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [code, setCode] = useState("");
  const [showError, setShowError] = useState(false);
  const [showError2, setShowError2] = useState(false);
  const [backendCodeErrors, setBackendCodeErrors] = useState([] as FormValidationResult);
  const [backendPasswordErrors, setBackendPasswordErrors] = useState([] as FormValidationResult);

  const schemaPassword = zPassword;
  const schemaPassword2 = zPassword.refine((_password2) => _password2 === password, {
    message: "passwords does not match",
  });
  const validationPassword = schemaPassword.safeParse(password);
  const validationPassword2 = schemaPassword2.safeParse(password2);

  const disabled = isLoading;
  const isCodeValid = code.length === CONFIRMATION_CODE_SIZE;
  const submitDisabled = isLoading || !validationPassword.success || !validationPassword2.success || !isCodeValid;

  const logout = useLogout();

  const handleSubmit = async () => {
    setIsLoading(true);
    await (
      await forgotPasswordConfirm(backendFetcher, { username, code, password })
    ).match({
      success: async () => {
        await (
          await signIn(backendFetcher, { username, password })
        ).match({
          success: async (loginResponse) => {
            // Todo: remove once we get rid of the Amplify
            try {
              await Auth.signIn({ username, password });
              // By setting the auth state, the user data will be refreshed by effect in SecureRouter
              setUserAuthState(mapLoginDataToUserState(loginResponse)); // <---- Leave only this
              navigate(route.dashboard()); // <---- Leave only this
            } catch (e: unknown) {
              toastErrorHandler(e);
            }
            // -------------------------------------------
          },
          businessMatcher: {
            notAuthorized: toastErrorHandler,
            userNotFound: toastErrorHandler,
            userNotConfirmed: toastErrorHandler,
          },
          unexpected: toastErrorHandler,
          unauthorized: logout,
        });
      },
      businessMatcher: {
        codeMismatch: (e) => setBackendCodeErrors([e.message]),
        expiredCode: (e) => setBackendCodeErrors([e.message]),
        invalidPassword: (e) => setBackendPasswordErrors([e.message]),
        limitExceeded: (e) => setBackendCodeErrors([e.message]),
        userNotFound: toastErrorHandler,
      },
      unexpected: toastErrorHandler,
      unauthorized: logout,
    });
    setIsLoading(false);
  };

  return (
    <Flex direction="col" gap={8} items={"center"}>
      <ForgotPasswordLockIcon />

      <Flex direction="col" gap={0} width={"w-full"}>
        <Flex direction="col" gap={2}>
          <Flex direction="row" justify="between" gap={2}>
            <Heading3>Reset Password?</Heading3>
          </Flex>
          <Flex>Your new password must be different from previously used passwords.</Flex>
        </Flex>

        <FieldGroup
          label={"New Password"}
          validation={[...(showError ? zodErrorsToString(validationPassword) : []), ...backendPasswordErrors]}
        >
          <InputText
            id="password"
            type="password"
            disabled={disabled}
            value={password}
            setValue={setPassword}
            border={!showError || validationPassword.success ? "neutral" : "red"}
            onBlur={() => setShowError(true)}
          />
        </FieldGroup>

        <FieldGroup label={"Confirm Password"} validation={showError2 ? zodErrorsToString(validationPassword2) : []}>
          <InputText
            id="confirmPassword"
            type="password"
            disabled={disabled}
            value={password2}
            setValue={setPassword2}
            border={!showError2 || validationPassword2.success ? "neutral" : "red"}
            onBlur={() => setShowError2(true)}
          />
        </FieldGroup>

        <ConfirmationCodeForm
          errors={backendCodeErrors}
          onSubmit={setCode}
          isLoading={isLoading}
          label={"Email Confirmation Code"}
        />

        <Flex verticalMargin={4}>
          <Button
            fill
            data-testid="setNewPasswordButton"
            id="setNewPasswordButton"
            primary={true}
            loading={isLoading}
            disabled={submitDisabled}
            onClick={handleSubmit}
          >
            Set New Password
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};
